import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import AppLogo from '../components/EllieAvatar';

export default function PremiumWelcome() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setUser = useAppStore((s) => s.setUser);
  const user = useAppStore((s) => s.user);

  const sessionId = params.get('session_id');
  const billing = params.get('billing');

  useEffect(() => {
    // Stripe redirects here after successful payment.
    // Update local tier + subscription details immediately; webhook also updates Cosmos DB.
    if (user) {
      setUser({
        ...user,
        tier: 'premium',
        premiumSince: user.premiumSince ?? new Date().toISOString(),
        subscriptionInterval: (billing === 'monthly' || billing === 'annual') ? billing : undefined,
      });
    }
    const timer = setTimeout(() => navigate('/'), 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-sky-50 flex flex-col items-center justify-center px-6 text-center">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 18 }}
        className="mb-6"
      >
        <AppLogo size={96} />
      </motion.div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="space-y-4"
      >
        <div className="text-5xl">🎉</div>
        <h1 className="font-display text-3xl font-extrabold text-primary-800">
          Welcome to Premium!
        </h1>
        <p className="text-gray-500 text-base max-w-xs mx-auto leading-relaxed">
          You now have full access to Practice Mode, parent guides, family challenges, and everything else KidWise offers.
        </p>

        <div className="bg-white rounded-3xl p-5 shadow-card max-w-xs mx-auto mt-4">
          <p className="font-bold text-gray-800 text-sm mb-3">What's unlocked for you:</p>
          <ul className="text-left space-y-2">
            {[
              '🧠 Practice Mode',
              '🏆 Weekly Family Challenges',
              '📥 Parent Conversation Guides',
              '📴 Offline Mode',
            ].map((f) => (
              <li key={f} className="text-sm text-gray-600">{f}</li>
            ))}
          </ul>
        </div>

        {sessionId && (
          <p className="text-gray-300 text-xs">Session: {sessionId.slice(0, 16)}…</p>
        )}

        <p className="text-gray-400 text-sm">Taking you to the app…</p>

        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-8 py-3.5 rounded-2xl font-bold text-base shadow-md hover:bg-primary-700 transition-colors"
        >
          Start Exploring →
        </button>
      </motion.div>
    </div>
  );
}
